<template>
  <section
    class="canvas-stage"
    :class="{
      'canvas-stage--dragging-over': draggingOver,
      'canvas-stage--has-bg': hasBackgroundImage,
    }"
    @dragenter.capture.prevent.stop="onDragEnter"
    @dragover.capture.prevent.stop="onDragOver"
    @dragleave.capture.prevent.stop="onDragLeave"
    @drop.capture.prevent.stop="onDrop"
  >
    <div class="canvas-stage__inner">
      <div
        class="canvas-stage__frame"
        :style="{
          'aspect-ratio': `${canvasWidth} / ${canvasHeight}`,
        }"
      >
        <canvas
          ref="editorCanvasEl"
          class="canvas-stage__canvas"
        ></canvas>

        <Transition
          name="fade-zoom"
          mode="out-in"
          appear
        >
          <div
            v-if="!hasBackgroundImage"
            class="canvas-stage__empty-state"
            role="button"
            tabindex="0"
            @click="backgroundStore.triggerImagePicker()"
            @keydown.enter.space.prevent="backgroundStore.triggerImagePicker()"
          >
            <IconBackground/>
            <p>
              <span class="desktop-only">Sleep een foto naar het canvas of klik om een foto toe te voegen.</span>
              <span class="mobile-only">Tik om een foto toe te voegen.</span>
            </p>
          </div>
        </Transition>
      </div>
    </div>

    <Transition name="fade-zoom">
      <div
        v-if="draggingOver"
        class="canvas-stage__drop-indicator"
      >
        <span class="canvas-stage__drop-indicator-text">Drop it like it's hot! <span class="canvas-stage__drop-indicator-emoji">🔥</span></span>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import IconBackground from '@/components/svg/IconBackground.vue';
  import type { FabricImage } from '@/fabric';
  import { activeFabricCanvas, FabricCanvasAugmented, setActiveFabricCanvas } from '@/fabric/canvas';
  import { useBackgroundStore } from '@/stores/useBackgroundStore';
  import { useFormStore, type ColorId } from '@/stores/useFormStore';
  import { useImagesStore } from '@/stores/useImagesStore';
  import { ImageService } from '@/services/ImageService';

  const rawBannerAssetUrls = import.meta.glob('@/assets/banners/*/*.png', {
    eager: true,
    import: 'default',
  }) as Record<string, string>;

  type ThemeLayer = {
    colorId: ColorId;
    themeId: string;
  };

  const bannerAssetUrlsByColorAndTheme = Object.entries(rawBannerAssetUrls)
    .reduce<Record<string, Record<string, string>>>((acc, [fullPath, assetUrl]) => {
      const matches = fullPath.match(/\/banners\/([^/]+)\/([^/]+)\.png$/);

      if (matches == null) {
        return acc;
      }

      const [, colorId, themeId] = matches;

      if (colorId == null || themeId == null) {
        return acc;
      }

      acc[colorId] ??= {};
      acc[colorId][themeId] = assetUrl;

      return acc;
    }, {});

  const editorCanvasEl = useTemplateRef('editorCanvasEl');

  const backgroundStore = useBackgroundStore();
  const formStore = useFormStore();
  const imagesStore = useImagesStore();
  const {
    hasBackgroundImage,
    canvasWidth,
    canvasHeight,
  } = storeToRefs(backgroundStore);
  const { selectedThemeIds, selectedThemeColorIds } = storeToRefs(formStore);

  const draggingOver = shallowRef(false);
  const dragDepth = shallowRef(0);
  const themeBannerImages = shallowRef<FabricImage[]>([]);
  const bannerSyncRunId = shallowRef(0);

  onMounted(() => {
    if (editorCanvasEl.value == null) {
      return;
    }

    const canvas = new FabricCanvasAugmented(editorCanvasEl.value, {
      width: backgroundStore.canvasWidth,
      height: backgroundStore.canvasHeight,
      preserveObjectStacking: true,
      selection: false,
      enableRetinaScaling: false,
      backgroundColor: '#ffffff',
    });

    setActiveFabricCanvas(canvas);
    void syncThemeBannersOnCanvas();
  });

  onBeforeUnmount(() => {
    clearThemeBannersFromCanvas();
  });

  watch([selectedThemeIds, selectedThemeColorIds, hasBackgroundImage], () => {
    void syncThemeBannersOnCanvas();
  }, {
    deep: true,
    immediate: true,
  });

  function getBannerAssetUrl (colorId: string, themeId: string): string | null {
    return bannerAssetUrlsByColorAndTheme[colorId]?.[themeId] ?? null;
  }

  function clearThemeBannersFromCanvas () {
    if (!activeFabricCanvas || themeBannerImages.value.length === 0) {
      themeBannerImages.value = [];
      return;
    }

    activeFabricCanvas.remove(...themeBannerImages.value);
    themeBannerImages.value = [];
    activeFabricCanvas.discardActiveObject();
    activeFabricCanvas.renderAll();
  }

  async function syncThemeBannersOnCanvas () {
    const currentRunId = ++bannerSyncRunId.value;

    clearThemeBannersFromCanvas();

    if (!activeFabricCanvas || hasBackgroundImage.value === false) {
      return;
    }

    const themeLayersTopToBottom = selectedThemeIds.value
      .map((themeId, index) => {
        if (themeId == null || themeId === '') {
          return null;
        }

        const colorId = selectedThemeColorIds.value[index];

        if (colorId == null) {
          return null;
        }

        return {
          colorId,
          themeId,
        };
      })
      .filter((layer): layer is ThemeLayer => layer !== null);

    if (themeLayersTopToBottom.length === 0) {
      return;
    }

    const themeLayersBottomToTop = [...themeLayersTopToBottom].reverse();

    const loadedThemeBanners = await Promise.all(themeLayersBottomToTop.map(async ({ colorId, themeId }) => {
      const assetUrl = getBannerAssetUrl(colorId, themeId);

      if (assetUrl == null) {
        return null;
      }

      const bannerImage = await ImageService.createFabricImageFromUrl(assetUrl);
      const imageWidth = bannerImage.width ?? backgroundStore.canvasWidth;
      const scale = backgroundStore.canvasWidth / imageWidth;

      bannerImage.scale(scale);
      bannerImage.originX = 'left';
      bannerImage.originY = 'bottom';
      bannerImage.left = 0;
      bannerImage.selectable = false;
      bannerImage.evented = false;
      bannerImage.hasControls = false;
      bannerImage.hoverCursor = 'default';
      bannerImage.moveCursor = 'default';
      bannerImage.lockMovementX = true;
      bannerImage.lockMovementY = true;
      bannerImage.lockRotation = true;
      bannerImage.lockScalingX = true;
      bannerImage.lockScalingY = true;
      bannerImage.lockScalingFlip = true;

      return bannerImage;
    }));

    if (currentRunId !== bannerSyncRunId.value || !activeFabricCanvas) {
      return;
    }

    const nextThemeBannerImages = loadedThemeBanners.filter((bannerImage): bannerImage is FabricImage => bannerImage !== null);

    if (nextThemeBannerImages.length === 0) {
      return;
    }

    let stackedHeightFromBottom = 0;

    for (const bannerImage of nextThemeBannerImages) {
      bannerImage.top = backgroundStore.canvasHeight - stackedHeightFromBottom - 150;
      stackedHeightFromBottom += bannerImage.getScaledHeight() + 11;
      activeFabricCanvas.add(bannerImage);
    }

    themeBannerImages.value = nextThemeBannerImages;
    activeFabricCanvas.renderAll();
  }

  function isFileDragEvent (event: DragEvent) {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  function resetDragState () {
    dragDepth.value = 0;
    draggingOver.value = false;
  }

  function onDragEnter (event: DragEvent) {
    if (!isFileDragEvent(event)) {
      return;
    }

    dragDepth.value += 1;
    draggingOver.value = true;
  }

  function onDragOver (event: DragEvent) {
    if (!isFileDragEvent(event)) {
      return;
    }

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    draggingOver.value = true;
  }

  function onDragLeave (event: DragEvent) {
    if (!isFileDragEvent(event)) {
      return;
    }

    dragDepth.value = Math.max(0, dragDepth.value - 1);

    if (dragDepth.value === 0) {
      draggingOver.value = false;
    }
  }

  function onDrop (event: DragEvent) {
    const [file] = Array.from(event.dataTransfer?.files ?? []);
    resetDragState();

    if (file == null) {
      return;
    }

    imagesStore.pipeFileAsBase64To({
      file,
      handler: backgroundStore.setBackgroundImage,
    });
  }
</script>

<style lang="scss">
  .canvas-stage {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    position: relative;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    padding: 24px 32px;
    overflow: hidden;

    @include mobile-view {
      padding: 18px;
    }

    &:not(&--has-bg) .canvas-container {
      display: none;
    }

    &__inner {
      flex: 1 1 auto;
      display: flex;
      align-items: stretch;
      justify-content: stretch;

      // overflow: hidden;
    }

    &__frame {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      @include desktop-view {
        aspect-ratio: auto !important;
      }
    }

    .canvas-container {
      width: 100% !important;
      height: 100% !important;
      position: absolute !important;
      inset: 0;
    }

    .lower-canvas {
      outline: 2px dashed var(--color-gray-500);
    }

    .lower-canvas,
    .upper-canvas {
      position: absolute !important;
      inset: 50% auto auto 50% !important;
      width: auto !important;
      height: auto !important;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      translate: -50% -50%;
    }

    &__empty-state {
      position: absolute;
      top: 50%;
      left: 50%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      pointer-events: auto;
      cursor: pointer;
      text-align: center;
      width: max-content;
      max-width: calc(100% - 36px);
      padding: 42px 42px 36px;
      border-radius: 10px;
      border: 2px dashed color-opacity(--color-black, 16%);
      color: var(--color-gray-700);
      user-select: none;
      outline: none !important;
      contain: paint layout;

      --empty-state-scale: 1;

      transform: translate(-50%, -50%) scale(var(--empty-state-scale));
      transition: transform var(--speed-medium), color var(--speed-medium);

      @media (hover: hover) {
        &:hover {
          --empty-state-scale: 1.015;

          color: var(--color-black);
        }
      }

      &:focus-visible {
        --empty-state-scale: 1.015;

        color: var(--color-black);
        border-color: var(--color-primary);
      }

      &:active {
        --empty-state-scale: 0.99;

        transition-duration: var(--speed-instant);
      }

      svg {
        width: 38px;
        height: 38px;
      }

      p {
        margin: 0;
        max-width: 26ch;
        line-height: 1.45;
        text-wrap: balance;
      }
    }

    &__drop-indicator {
      position: absolute;
      inset: 24px 32px;
      z-index: 5;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      border: 2px dashed var(--color-primary);
      border-radius: 10px;
      background: color-opacity(--color-black, 20%);
      color: var(--color-white);
      text-align: center;
      text-shadow: 0 0 10px color-opacity(--color-black, 10%);

      @include mobile-view {
        inset: 18px;
      }
    }

    &__drop-indicator-text {
      font-size: clamp(30px, 2.6vw, 42px);
      font-weight: 700;
      line-height: 1.05;
    }

    &__drop-indicator-emoji {
      display: inline-block;
      margin-left: 10px;
      font-size: 220%;
      vertical-align: middle;
      animation: shake 0.3s infinite;
    }
  }
</style>
